
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  Divider,
  Box,
  Grid,
  Button,
  IconButton,
  Autocomplete,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import DatePicker from '@mui/lab/DatePicker';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import _ from 'lodash';
import ContactFormModal from './ContactFormModal';
import ContactFormFields from './ContactFormFields';
import { contactCreate } from '../../redux/actions';
import { validateThaiAddress } from '../../utils/functions/thaiZipCascade';
import useZipCascade from '../../hooks/useZipCascade';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';
import { emptyValues as emptyClientValues } from '../../views/Customer/client.constants';
import { sanitizeIdentifier } from '../../utils/functions/sanitizeIdentifier';

const FS = '0.875rem';
const FSI = `${FS} !important`;

const STATUS_MAP = {
  PENDING:     { label: 'รอดำเนินการ',                color: '#64748b', bg: '#f1f5f9' },
  IN_PROGRESS: { label: 'กำลังดำเนินการ',             color: '#2563eb', bg: '#eff6ff' },
  DELIVERY:    { label: 'งาน100%รอรับเงินงวดสุดท้าย', color: '#d97706', bg: '#fffbeb' },
  Warranty:    { label: 'มีWarrant/Guarantee',         color: '#7c3aed', bg: '#f5f3ff' },
  COMPLETED:   { label: 'ไม่มีภาระผูกพัน',           color: '#059669', bg: '#ecfdf5' },
};
const F = {
  sx: {
    '& .MuiInputBase-input': { fontSize: FSI },
    '& .MuiFormLabel-root':  { fontSize: FSI },
    '& .MuiSelect-select':   { fontSize: FSI },
  },
};

export default function ProjectForm({
  Controller,
  control,
  employee,
  contacts = [],
  date,
  setDate,
  dateEnd,
  setDateEnd,
  includeVAT,
  setIncludeVAT,
  cost,
  setCost,
  extraGpsSites = [],
  setExtraGpsSites,
  selectedClient,
  setSelectedClient,
  setValue,
}) {
  const dispatch = useDispatch();
  const [extraContacts, setExtraContacts] = useState([]);
  const [projectNumberWarning, setProjectNumberWarning] = useState('');
  const clientContacts = [...contacts, ...extraContacts].filter((c) => c.role === 'client');

  const [addClientOpen, setAddClientOpen] = useState(false);
  const [addClientSubmitError, setAddClientSubmitError] = useState('');
  const {
    handleSubmit: handleAddClientSubmit, control: addClientControl, reset: addClientReset, setValue: addClientSetValue, watch: addClientWatch,
  } = useForm({ defaultValues: emptyClientValues });
  const { cascade: addClientCascade, setZipSelections: setAddClientZipSelections } = useZipCascade(addClientWatch, addClientSetValue);

  const handleAddClientOpen = () => {
    setAddClientZipSelections({ province: null, district: null });
    setAddClientSubmitError('');
    setAddClientOpen(true);
  };
  const handleAddClientClose = () => { setAddClientOpen(false); addClientReset(emptyClientValues); };

  const onAddClientSubmit = async (data) => {
    const zipError = validateThaiAddress(data);
    if (zipError) { setAddClientSubmitError(zipError); return; }
    try {
      const created = await dispatch(contactCreate({ ...data, role: 'client' }));
      if (created) {
        setExtraContacts((prev) => [...prev, created]);
        setSelectedClient(created);
        setValue('customer', created.name || '');
        setValue('client_id', created._id || null);
      }
      handleAddClientClose();
    } catch (error) {
      console.error('Failed to create client:', error);
    }
  };

  useEffect(() => {
    if (selectedClient) {
      const addr = [selectedClient.address1, selectedClient.address2, selectedClient.address3]
        .filter(Boolean)
        .join(' ');
      setValue('place', addr);
    }
  }, [selectedClient]);

  return (
    <Box>
      {/* ── ข้อมูลโครงการ ── */}
      <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.75 }}>
        ข้อมูลโครงการ
      </Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={4}>
          <Controller
            name="project_number"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                onChange={(e) => {
                  const { cleaned, foundDescriptions } = sanitizeIdentifier(e.target.value);
                  field.onChange(cleaned);
                  setProjectNumberWarning(
                    foundDescriptions.length
                      ? `พบอักขระที่มีปัญหา (แสดงเป็น � ในช่อง กดลบออกได้เลย): ${foundDescriptions.join(', ')}`
                      : '',
                  );
                }}
                label="Project ID"
                fullWidth
                size="small"
                error={Boolean(projectNumberWarning)}
                helperText={projectNumberWarning}
                {...F}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={8}>
          <Controller
            name="name"
            control={control}
            defaultValue=""
            rules={{ required: true }}
            render={({ field }) => (
              <TextField {...field} label="Project Name" fullWidth size="small" required {...F} />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Controller
            name="operation_status"
            control={control}
            defaultValue="PENDING"
            render={({ field }) => (
                <FormControl fullWidth size="small" {...F}>
                  <InputLabel size="small">Status</InputLabel>
                  <Select
                    {...field}
                    label="Status"
                    sx={{ '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 0.75 } }}
                  >
                    {Object.entries(STATUS_MAP).map(([key, s]) => (
                      <MenuItem key={key} value={key} sx={{ fontSize: FS, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color, flexShrink: 0, display: 'inline-block' }} />
                        {s.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={8}>
          <Controller
            name="description"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField {...field} label="Description" fullWidth size="small" multiline rows={2} {...F} />
            )}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 1.5 }} />

      {/* ── การเงิน ── */}
      <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.75 }}>
        การเงิน
      </Typography>
      <Grid container spacing={1.5} alignItems="center">
        <Grid item xs={12} sm={4}>
          <TextField
            label="ราคา (Price)"
            fullWidth type="number" size="small"
            value={cost}
            onChange={(e) => setCost(Number(e.target.value))}
            {...F}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Controller
            name="labur_cost"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField {...field} label="Labor Cost" fullWidth size="small" {...F} />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeVAT}
                onChange={(e) => setIncludeVAT(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={<span style={{ fontSize: FS }}>Include VAT</span>}
            sx={{ ml: 0 }}
          />
          {!includeVAT && cost > 0 && (
            <Typography variant="caption" color="text.secondary" display="block">
              VAT 7%: {(cost * 0.07).toFixed(2)} | รวม: {(cost * 1.07).toFixed(2)}
            </Typography>
          )}
        </Grid>
      </Grid>

      <Divider sx={{ my: 1.5 }} />

      {/* ── ลูกค้า ── */}
      <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.75 }}>
        ลูกค้า
      </Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={12}>
          <Controller
            name="client_id"
            control={control}
            defaultValue={null}
            render={({ field }) => <input type="hidden" {...field} />}
          />
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
            <Autocomplete
              size="small"
              sx={{ flex: 4 }}
              options={clientContacts}
              getOptionLabel={(option) => option.name || ''}
              value={selectedClient}
              onChange={(e, newValue) => {
                setSelectedClient(newValue);
                setValue('customer', newValue?.name || '');
                setValue('client_id', newValue?._id || null);
              }}
              renderInput={(params) => (
                <TextField {...params} label="ผู้ว่าจ้าง (Customer)" {...F} />
              )}
              isOptionEqualToValue={(option, value) => option._id === value?._id}
            />
            <Button
              variant="outlined"
              size="small"
              color="primary"
              startIcon={<PersonAddAlt1Icon fontSize="small" />}
              onClick={handleAddClientOpen}
              sx={{ flex: 1, mt: 0.25, whiteSpace: 'nowrap' }}
            >
              เพิ่มลูกค้า
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="place"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField {...field} label="สถานที่ (Location)" fullWidth size="small" {...F} />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="google_drive_url"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField {...field} label="URL Google Drive" fullWidth size="small" {...F} />
            )}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 1.5 }} />

      {/* ── วันที่ ── */}
      <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.75 }}>
        วันที่
      </Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              value={date}
              onChange={(newValue) => setDate(newValue)}
              renderInput={(params) => <TextField {...params} size="small" fullWidth {...F} />}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="End Date"
              value={dateEnd}
              onChange={(newValue) => setDateEnd(newValue)}
              renderInput={(params) => <TextField {...params} size="small" fullWidth {...F} />}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>

      <Divider sx={{ my: 1.5 }} />

      {/* ── GPS ── */}
      <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.75 }}>
        GPS จุดหลัก
      </Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6}>
          <Controller
            name="gps.lat"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField {...field} label="Latitude" fullWidth size="small" {...F} />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="gps.lon"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField {...field} label="Longitude" fullWidth size="small" {...F} />
            )}
          />
        </Grid>
      </Grid>

      {setExtraGpsSites && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            ไซต์ GPS เพิ่มเติม
          </Typography>
          {extraGpsSites.map((row, idx) => (
            <Grid container spacing={1} key={idx} alignItems="center" sx={{ mb: 0.75 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="ชื่อไซต์"
                  value={row.name}
                  onChange={(e) => {
                    const next = [...extraGpsSites];
                    next[idx] = { ...next[idx], name: e.target.value };
                    setExtraGpsSites(next);
                  }}
                  fullWidth size="small"
                  placeholder={`ไซต์ ${idx + 1}`}
                  {...F}
                />
              </Grid>
              <Grid item xs={5} sm={3}>
                <TextField
                  label="Lat" type="number"
                  inputProps={{ step: 0.00001 }}
                  value={row.lat}
                  onChange={(e) => {
                    const next = [...extraGpsSites];
                    next[idx] = { ...next[idx], lat: e.target.value };
                    setExtraGpsSites(next);
                  }}
                  fullWidth size="small" {...F}
                />
              </Grid>
              <Grid item xs={5} sm={3}>
                <TextField
                  label="Lon" type="number"
                  inputProps={{ step: 0.00001 }}
                  value={row.lon}
                  onChange={(e) => {
                    const next = [...extraGpsSites];
                    next[idx] = { ...next[idx], lon: e.target.value };
                    setExtraGpsSites(next);
                  }}
                  fullWidth size="small" {...F}
                />
              </Grid>
              <Grid item xs={2}>
                <IconButton
                  aria-label="ลบไซต์"
                  onClick={() => setExtraGpsSites(extraGpsSites.filter((_, i) => i !== idx))}
                  size="small" color="error"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          <Button
            variant="outlined" size="small"
            onClick={() => setExtraGpsSites([...extraGpsSites, { name: '', lat: '', lon: '' }])}
            sx={{ mt: 0.25 }}
          >
            เพิ่มไซต์
          </Button>
        </Box>
      )}

      <Divider sx={{ my: 1.5 }} />

      {/* ── Project Manager ── */}
      <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.75 }}>
        Project Manager
      </Typography>
      <Controller
        name="engineer"
        control={control}
        defaultValue={employee ? employee?.engineer?._id : ''}
        rules={{ required: true }}
        render={({ field }) => (
          <FormControl fullWidth required {...F}>
            <InputLabel size="small">Project Manager</InputLabel>
            <Select {...field} label="Project Manager" size="small" fullWidth>
              {_.size(employee) ? (
                _.map(employee, (row) => (
                  <MenuItem key={row.id} value={row.id} sx={{ fontSize: FS }}>
                    {formatEmployeeThaiName(row)}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled value="" sx={{ fontSize: FS }}>
                  <em>No Data Available</em>
                </MenuItem>
              )}
            </Select>
          </FormControl>
        )}
      />

      <ContactFormModal
        open={addClientOpen}
        onClose={handleAddClientClose}
        title="เพิ่มลูกค้าใหม่"
        subtitle="กรอกข้อมูลลูกค้าองค์กรหรือบุคคลธรรมดา"
        onSubmit={handleAddClientSubmit(onAddClientSubmit)}
        error={addClientSubmitError}
      >
        <ContactFormFields control={addClientControl} cascade={addClientCascade} setZipSelections={setAddClientZipSelections} />
      </ContactFormModal>
    </Box>
  );
}
